import { useTrainingDraft } from '../../hooks/useTrainingDraft';
import { TrainingHelp } from './LearningWorkspace';
// pages/training/TrainingPlayerPage.tsx
import React, { useMemo, useState } from 'react';
import {
  Alert,
  AspectRatio,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ArrowLeft,
  ArrowSquareOut,
  CheckCircle,
  Clock,
  DownloadSimple,
  Warning,
} from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useMyTrainingDetail,
  useRecordMyTrainingProgress,
  useCompleteMyTraining,
  isUnlinkedLoginError,
  trainingErrorMessage,
} from '../../hooks/useTraining';
import type { MyTrainingChecklistEntry, MyTrainingDetail, MyTrainingQuiz } from '../../types/training';
import { contentTypeLabels, credentialTypeLabels, getDeadlineChip } from './constants';
import { TrainingVideoPlayer } from './TrainingVideoPlayer';
import { TrainingPdfViewer } from './TrainingPdfViewer';
import { TrainingQuizRunner } from './TrainingQuizRunner';

/** Enough of a video to count as watched. */
const WATCHED_THRESHOLD = 90;

/** Embeddable player URL for a hosted video, or null if we can only link out. */
const getVideoEmbedUrl = (url: string): string | null => {
  const youtube = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/
  );
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`;

  const vimeo = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  return null;
};

/** The API supplies unique ids even for legacy string-only checklists. */
const checklistLabels = (content: unknown): { id: string; text: string; required: boolean }[] => Array.isArray(content) ? content.map((entry, index) => { const line = entry as MyTrainingChecklistEntry; return { id: line.id || `legacy:${index}`, text: typeof entry === 'string' ? entry : line.text || line.item || '', required: line.required !== false }; }).filter(line => line.text) : [];

export const TrainingPlayer: React.FC<{ preview?: MyTrainingDetail; previewQuiz?: MyTrainingQuiz & { answerKey: Record<string, string> } }> = ({ preview, previewQuiz }) => {
  const { progressId } = useParams<{ progressId: string }>();
  const navigate = useNavigate();

  const { data: loaded, isLoading, error } = useMyTrainingDetail(preview ? undefined : progressId);
  const data = preview ?? loaded;
  const [previewCompleted, setPreviewCompleted] = useState(false);
  const recordProgress = useRecordMyTrainingProgress();
  const completeTraining = useCompleteMyTraining();

  // Null until playback reports something: until then the bar shows where the
  // learner left off last time, per the server.
  const [watchedTick, setWatchedTick] = useState<number | null>(null);
  const checklistDraft = useTrainingDraft<string[]>(progressId || '', 'completed_items', data?.progress.draft?.completed_items || [], !preview);
  const checked = checklistDraft.value;
  const [openedLink, setOpenedLink] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const checklist = useMemo(
    () => (data?.training_item.content_type === 'checklist' ? checklistLabels(data.structured_content) : []),
    [data]
  );

  if (isLoading && !preview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Button
          variant="plain"
          color="neutral"
          startDecorator={<ArrowLeft />}
          onClick={() => navigate('/training')}
          sx={{ mb: 2 }}
        >
          My Training
        </Button>
        <Alert color={isUnlinkedLoginError(error) ? 'warning' : 'danger'} startDecorator={<Warning />}>
          {trainingErrorMessage(error, 'This training could not be opened.')}
        </Alert>
      </Box>
    );
  }

  const { progress, training_item: item, file_url: fileUrl } = data;
  const watched = watchedTick ?? progress.watch_percentage;
  const isCompleted = previewCompleted || progress.status === 'completed';
  const deadline = getDeadlineChip(progress);
  const credential = credentialTypeLabels[progress.credential_type];
  const embedUrl = item.external_url ? getVideoEmbedUrl(item.external_url) : null;
  // Nothing to watch or read in the app: the learner has to leave for it.
  const isLinkOut =
    item.content_type === 'link' || (item.content_type === 'video' && !fileUrl && !embedUrl);

  const handleReport = (percentage: number, positionSeconds: number) => {
    if (preview) return;
    recordProgress.mutate({
      progressId: progressId as string,
      watchPercentage: percentage,
      progressData: { video_position_seconds: Math.round(positionSeconds) },
    });
  };

  const toggleChecklistItem = (id: string) => checklistDraft.set(checked.includes(id) ? checked.filter(value => value !== id) : [...checked, id]);

  const handleComplete = async () => {
    if (preview) { setPreviewCompleted(true); return; }
    try {
      await checklistDraft.flush();
      if (item.content_type === 'video' && fileUrl) await recordProgress.mutateAsync({ progressId: progressId as string, watchPercentage: watched });
      await completeTraining.mutateAsync({
        progressId: progressId as string,
        checklistCompleted: item.content_type === 'checklist' ? checked : undefined,
        confirmation: confirmed,
      });
      toast.success('Training marked complete');
    } catch (completeError) {
      toast.error(trainingErrorMessage(completeError, 'This could not be marked complete.'));
    }
  };

  // What still stands between the learner and "complete", in their words.
  const needsConfirmation = item.requires_completion_confirmation || item.content_type === 'link' || (item.content_type === 'video' && !fileUrl);
  const blockedReason = (): string | null => {
    if (data.prerequisite_ids?.length) return 'Complete the earlier courses in this learning path before finishing this item.';
    if ((['video', 'link'].includes(item.content_type) && !fileUrl && !item.external_url) || (['pdf', 'scorm'].includes(item.content_type) && !fileUrl) || (item.content_type === 'article' && !item.article_content?.trim())) return 'This course is missing its content. Send HR a help request below.';
    if (item.content_type === 'video' && fileUrl && watched < WATCHED_THRESHOLD) {
      return `Watch at least ${WATCHED_THRESHOLD}% of the video to finish (you are at ${watched}%).`;
    }
    if (item.content_type === 'checklist' && (!checklist.length || checklist.some(line => line.required && !checked.includes(line.id)))) {
      return `Tick every item — ${checked.length} of ${checklist.length} done.`;
    }
    if (isLinkOut && item.external_url && !openedLink) {
      return 'Open it and work through it before marking this complete.';
    }
    if (needsConfirmation && !confirmed) {
      return 'Confirm you completed this training.';
    }
    return null;
  };

  const renderContent = () => {
    switch (item.content_type) {
      case 'video': {
        if (fileUrl) {
          return (
            <TrainingVideoPlayer
              src={fileUrl}
              captionsUrl={item.captions_url}
              title={item.title}
              initialPercentage={progress.watch_percentage}
              watchedPercentage={watched}
              onWatchedChange={setWatchedTick}
              onReport={handleReport}
            />
          );
        }
        if (embedUrl) {
          return (
            <AspectRatio ratio="16/9" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
              <iframe
                src={embedUrl}
                title={item.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ border: 'none' }}
              />
            </AspectRatio>
          );
        }
        if (item.external_url) {
          return (
            <Button
              component="a"
              href={item.external_url}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              endDecorator={<ArrowSquareOut />}
              onClick={() => setOpenedLink(true)}
            >
              Watch the video
            </Button>
          );
        }
        return <Alert color="warning">This video has not been uploaded yet. Ask HR.</Alert>;
      }

      case 'pdf':
        return fileUrl ? (
          <TrainingPdfViewer fileUrl={fileUrl} />
        ) : (
          <Alert color="warning">This document has not been uploaded yet. Ask HR.</Alert>
        );

      case 'article':
        return item.article_content ? (
          <Sheet variant="soft" sx={{ borderRadius: 'sm', p: { xs: 1.5, md: 2.5 } }}>
            <Typography level="body-md" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {item.article_content}
            </Typography>
          </Sheet>
        ) : (
          <Alert color="warning">This article has no content yet. Ask HR.</Alert>
        );

      case 'link':
        return item.external_url ? (
          <Stack spacing={1.5} sx={{ alignItems: 'stretch' }}>
            <Button
              component="a"
              href={item.external_url}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              endDecorator={<ArrowSquareOut />}
              onClick={() => setOpenedLink(true)}
            >
              Open the training
            </Button>
            <Typography level="body-xs" sx={{ color: 'neutral.500', wordBreak: 'break-all' }}>
              {item.external_url}
            </Typography>
          </Stack>
        ) : (
          <Alert color="warning">No link has been set for this training yet. Ask HR.</Alert>
        );

      case 'checklist':
        return checklist.length > 0 ? (
          <Stack spacing={1}>
            {checklist.map((line) => (
              <Sheet key={line.id} variant="outlined" sx={{ borderRadius: 'sm', p: 1.5 }}>
                <Checkbox
                  label={`${line.text}${line.required ? '' : ' (optional)'}`}
                  checked={isCompleted || checked.includes(line.id)}
                  disabled={isCompleted}
                  onChange={() => toggleChecklistItem(line.id)}
                  sx={{ width: '100%' }}
                />
              </Sheet>
            ))}
          </Stack>
        ) : (
          <Alert color="warning">This checklist has no items yet. Ask HR.</Alert>
        );

      case 'quiz':
        return <TrainingQuizRunner key={progressId} progressId={progressId as string} progress={progress} previewQuiz={previewQuiz} />;

      default:
        return fileUrl ? (
          <Button
            component="a"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="lg"
            endDecorator={<DownloadSimple />}
          >
            Open the {contentTypeLabels[item.content_type].toLowerCase()}
          </Button>
        ) : (
          <Alert color="warning">
            This {contentTypeLabels[item.content_type].toLowerCase()} has not been uploaded yet. Ask HR.
          </Alert>
        );
    }
  };

  const reason = blockedReason();

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 12, md: 3 } }}>
      {!preview && <Button
        variant="plain"
        color="neutral"
        startDecorator={<ArrowLeft />}
        onClick={() => navigate('/training')}
        sx={{ mb: 1, ml: -1 }}
      >
        My Training
      </Button>}
      {preview && <Alert sx={{ mb: 2 }}>Learner preview. Completion and quiz results stay in this preview; no learner records are changed.</Alert>}

      <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>
        {item.title}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 2 }}>
        {deadline && (
          <Chip size="sm" variant="soft" color={deadline.color}>
            {deadline.label}
          </Chip>
        )}
        {progress.is_mandatory && (
          <Chip size="sm" variant="soft" color="danger">
            Required
          </Chip>
        )}
        {credential && (
          <Chip size="sm" variant="outlined" color="neutral">
            {credential}
          </Chip>
        )}
        <Chip size="sm" variant="outlined" color="neutral">
          {contentTypeLabels[item.content_type]}
        </Chip>
        {item.estimated_duration_minutes > 0 && (
          <Chip size="sm" variant="outlined" color="neutral" startDecorator={<Clock />}>
            {item.estimated_duration_minutes} min
          </Chip>
        )}
      </Stack>

      {item.description && (
        <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
          {item.description}
        </Typography>
      )}

      <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        {renderContent()}
      </Card>

      {item.transcript && <Card sx={{ mb: 2 }}><Box component="details"><Typography component="summary">Read transcript</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{item.transcript}</Typography></Box></Card>}
      {item.content_type === 'checklist' && !isCompleted && <Alert color={checklistDraft.error ? 'warning' : 'neutral'} endDecorator={checklistDraft.error ? <Button onClick={() => checklistDraft.flush().catch(() => undefined)}>Retry saving</Button> : undefined}>{checklistDraft.status}</Alert>}
      {!preview && <TrainingHelp key={progressId} progressId={progressId as string} />}
      {data.prerequisite_ids?.length ? <Alert color="warning">Earlier learning-path courses are needed before this course can be completed. You can still review its content.</Alert> : null}
      {isCompleted && (
        <Alert color="success" startDecorator={<CheckCircle />} sx={{ mb: 2 }}>
          <Box>
            <Typography level="title-sm">
              Completed{progress.completed_at ? ` ${new Date(progress.completed_at).toLocaleDateString()}` : ''}
            </Typography>
            {progress.expires_at && (
              <Typography level="body-sm">
                Valid until {new Date(progress.expires_at).toLocaleDateString()} — you will be
                reassigned before it lapses.
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      {/* Quizzes finish by being graded, not by being declared done. */}
      {item.content_type !== 'quiz' && !isCompleted && (
        <Sheet
          variant="outlined"
          sx={{
            position: { xs: preview ? 'static' : 'fixed', md: 'static' },
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            p: { xs: 1.5, md: 2 },
            borderRadius: { xs: 0, md: 'sm' },
          }}
        >
          <Stack spacing={1}>
            {needsConfirmation && (
              <Checkbox
                label="I confirm I completed this training"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
            )}
            <Button
              size="lg"
              startDecorator={<CheckCircle />}
              disabled={!!reason}
              loading={completeTraining.isPending}
              onClick={handleComplete}
            >
              Mark complete
            </Button>
            {reason && (
              <Typography level="body-xs" sx={{ color: 'neutral.500', textAlign: 'center' }}>
                {reason}
              </Typography>
            )}
          </Stack>
        </Sheet>
      )}
    </Box>
  );
};

export const TrainingPlayerPage: React.FC = () => { const { progressId } = useParams(); return <TrainingPlayer key={progressId} />; };
export default TrainingPlayerPage;
